import { ChatSurveyElement } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyElement";
import { ChatSurveyMutationResult } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyMutationResult";
import { ChatSurveyNextAction } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyNextAction";
import { ChatSurveyState } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyState";
import { ChatSurveyTimelineUpdate } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyTimelineUpdate";
import { Instance, cast, types } from "mobx-state-tree";

const ChatSurveyElementSnapshot =
  types.frozen<ChatSurveyElement.ChatSurveyElementDTO>();
const ChatSurveyNextActionSnapshot =
  types.frozen<ChatSurveyNextAction.ChatSurveyNextActionDTO>();

export const ChatSurveyStore = types
  .model("ChatSurveyStore", {
    elements: types.optional(types.array(ChatSurveyElementSnapshot), []),
    timelineRevision: types.optional(types.number, 0),
    nextAction: types.maybeNull(ChatSurveyNextActionSnapshot),
  })
  .views((_store) => ({
    getElementKey(element: ChatSurveyElement.ChatSurveyElementDTO): string {
      switch (element.case) {
        case "WELCOME":
          return "welcome";
        case "QUESTION":
          return `question:${(element as ChatSurveyElement.QuestionDTO).respondentQuestionDetails.questionId}`;
        case "THANK_YOU":
          return "thank-you";
        default:
          throw new Error(`Unsupported chat survey element: ${element.case}`);
      }
    },
  }))
  .actions((store) => ({
    replaceAll(elements: ChatSurveyElement.ChatSurveyElement[]): void {
      store.elements = cast(elements.map((element) => element.toDTO()));
      store.timelineRevision += 1;
    },
    replaceFromQuestion(
      questionId: string,
      elements: ChatSurveyElement.ChatSurveyElement[],
    ): void {
      const anchorIndex = store.elements.findIndex(
        (element) =>
          element.case === "QUESTION" &&
          (element as ChatSurveyElement.QuestionDTO).respondentQuestionDetails
            .questionId === questionId,
      );
      if (anchorIndex < 0) {
        throw new Error(`Chat question ${questionId} is not in the timeline.`);
      }
      store.elements.splice(
        anchorIndex,
        store.elements.length - anchorIndex,
        ...elements.map((element) => element.toDTO()),
      );
      store.timelineRevision += 1;
    },
    setNextAction(nextAction: ChatSurveyNextAction.ChatSurveyNextAction): void {
      store.nextAction = cast(nextAction.toDTO());
    },
  }))
  .actions((store) => ({
    applyState(state: ChatSurveyState): void {
      store.replaceAll(state.elements);
      store.setNextAction(state.nextAction);
    },
    applyMutation(result: ChatSurveyMutationResult): void {
      if (
        result.timelineUpdate instanceof ChatSurveyTimelineUpdate.ReplaceAll
      ) {
        store.replaceAll(result.timelineUpdate.elements);
      } else if (
        result.timelineUpdate instanceof
        ChatSurveyTimelineUpdate.ReplaceFromQuestion
      ) {
        store.replaceFromQuestion(
          result.timelineUpdate.questionId.toDTO(),
          result.timelineUpdate.elements,
        );
      }
      store.setNextAction(result.nextAction);
    },
  }));

export const createChatSurveyStore = (): Instance<typeof ChatSurveyStore> =>
  ChatSurveyStore.create({ nextAction: null });
